/**
 * Адреса картинок, которые уже догрузились в этой вкладке.
 *
 * Зачем вне компонента: знание «картинка загружена» жило в `useState` и умирало вместе
 * с компонентом. При возврате на экран отсчёт начинался с нуля, и первый кадр рисовался
 * как «ещё не загрузилось» — у скина силуэт, у аватара пустота, хотя картинка давно
 * лежит в кэше браузера. Множество переживает размонтирование, поэтому возврат
 * начинается сразу с готового состояния.
 *
 * Сетевых запросов множество не делает: адрес попадает сюда только по факту `onLoad`
 * или уже готового элемента. Пробу через `new Image()` здесь заводить нельзя — она
 * стоила лишнего запроса на каждый скин мимо ленивой загрузки, её убрали намеренно.
 *
 * Живёт столько же, сколько вкладка: ровно ту сессию, в которой человек ходит по
 * экранам туда-сюда. Между перезагрузками ничего не сохраняем — кэш браузера и так
 * переживает перезагрузку сам, а вот угадывать за него на первом рендере страницы
 * нельзя: серверная разметка про кэш не знает, и расхождение сломало бы гидратацию.
 */

/** Потолок набора. В инвентаре и в истории дропов адресов много, а нужен только
 *  «недавно виденный» хвост: вытесняем самые старые. */
const MAX_TRACKED = 300;

const loaded = new Set<string>();

/**
 * Ключ набора — абсолютный адрес, а не строка из пропа.
 *
 * Один и тот же аватар приходит в компонент в двух видах: серверная разметка кладёт
 * `/api/users/avatar/<id>` (на сервере `getApiBaseUrl()` возвращает пустую строку), а
 * после гидратации тот же элемент получает `https://host/api/users/avatar/<id>`.
 * Браузер видит один ресурс и второй раз его не грузит, а набор по сырым строкам видел
 * бы два разных адреса и промахивался бы мимо уже загруженной картинки.
 */
function keyOf(src: string): string {
  if (typeof window === 'undefined') return src;
  try {
    return new URL(src, window.location.href).href;
  } catch {
    return src;
  }
}

/** Загружался ли этот адрес уже в этой вкладке. */
export function isImageLoaded(src: string | null | undefined): boolean {
  return !!src && loaded.has(keyOf(src));
}

/** Отметить адрес загруженным. Повторный вызов освежает его позицию в очереди. */
export function markImageLoaded(src: string | null | undefined): void {
  if (!src) return;
  // Set хранит порядок вставки, поэтому удаление перед добавлением двигает адрес
  // в конец очереди и спасает часто показываемые картинки от вытеснения.
  const key = keyOf(src);
  loaded.delete(key);
  loaded.add(key);
  if (loaded.size > MAX_TRACKED) {
    const oldest = loaded.values().next().value;
    if (oldest !== undefined) loaded.delete(oldest);
  }
}

/** Забыть адрес: картинка, которую браузер всё-таки выкинул из кэша, не должна
 *  притворяться готовой на следующем монтировании. */
export function forgetImageLoaded(src: string | null | undefined): void {
  if (src) loaded.delete(keyOf(src));
}

/** Только для тестов: набор должен быть пустым между проверками. */
export function resetLoadedImages(): void {
  loaded.clear();
}
